import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from './ui/Button';
import { useTranslation } from '../hooks/useTranslation';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  /** If set, user must type this exact text to confirm */
  typedConfirmation?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  typedConfirmation,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('confirm.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('confirm.cancel');
  const [typedValue, setTypedValue] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const canConfirm = typedConfirmation
    ? typedValue === typedConfirmation
    : true;

  // Reset typed value when dialog opens/closes
  useEffect(() => {
    if (open) {
      setTypedValue('');
      // Focus the input or first focusable element on open
      requestAnimationFrame(() => {
        if (typedConfirmation && inputRef.current) {
          inputRef.current.focus();
        } else {
          const first = overlayRef.current?.querySelector<HTMLElement>(
            'button:not([disabled])',
          );
          first?.focus();
        }
      });
    }
  }, [open, typedConfirmation]);

  // Escape key handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      // Focus trap
      if (e.key === 'Tab') {
        const focusable = overlayRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled])',
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [open, onCancel],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md rounded-xl bg-surface border border-border p-6 shadow-xl">
        <h2
          id="confirm-dialog-title"
          className="text-lg font-semibold text-text-primary mb-2"
        >
          {title}
        </h2>

        <div className="text-sm text-text-secondary mb-5">{message}</div>

        {typedConfirmation && (
          <div className="mb-5">
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              {t('confirm.typePrefix')}{' '}
              <span className="font-bold">{typedConfirmation}</span>{' '}
              {t('confirm.typeSuffix')}
            </label>
            <input
              ref={inputRef}
              type="text"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={typedConfirmation}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoComplete="off"
            />
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            {resolvedCancelLabel}
          </Button>
          <Button
            variant={variant}
            onClick={onConfirm}
            disabled={!canConfirm}
            isLoading={isLoading}
          >
            {resolvedConfirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
